import React from 'react';
import { Zap, BatteryCharging, ShieldCheck } from 'lucide-react';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };

/**
 * RichEvBenefits — the laderabat / Saving Mode / serviceaftale value block.
 * Reads pricing_data.evData (laderabatYearly, smartChargeYearly, serviceAgreementYearly,
 * subscriptionCoveredPct, hasSurplus, surplusYearly, evKwh).
 */
export default function RichEvBenefits({ evData = {}, heading = 'Har du elbil? Så bliver det endnu billigere' }) {
  const fmt = (n) => Math.round(n || 0).toLocaleString('da-DK');
  const laderabat = evData.laderabatYearly || 792;
  const smart = evData.smartChargeYearly || 2526;
  const service = evData.serviceAgreementYearly || 828;
  const evKwh = evData.evKwh || 7920;
  const pct = Math.min(100, evData.subscriptionCoveredPct ?? 100);
  const hasSurplus = evData.hasSurplus ?? laderabat > 216;
  const surplus = evData.surplusYearly ?? Math.max(0, laderabat - 216);
  const total = laderabat + smart + service;

  const cards = [
    { icon: Zap, label: 'Laderabat', value: `${fmt(laderabat)} kr./år`, sub: '10 øre/kWh på opladning via Saving Mode' },
    { icon: BatteryCharging, label: 'Saving Mode', value: `op til ${fmt(smart)} kr./år`, sub: 'Lader automatisk i de billigste timer' },
    { icon: ShieldCheck, label: 'Serviceaftale', value: `${fmt(service)} kr./år`, sub: 'Inkl. elafgift-refusion – 0 kr./md.' },
  ];

  return (
    <section className="py-12 md:py-16 bg-[#F6F3EC]" id="elbil">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3 text-[#1F2E33]" style={serif}>{heading}</h2>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
            Ved {fmt(evKwh)} kWh/år hjemmeopladning sparer du ekstra op til <strong>{fmt(total)} kr./år</strong> med laderabat, Saving Mode og serviceaftale – alt til 0 kr./md.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            {cards.map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-[#263A43]/10 shadow-sm">
                <c.icon className="w-6 h-6 text-[#D4A62A] mb-3" />
                <p className="text-xs font-bold uppercase tracking-wide text-[#5E7B8C]">{c.label}</p>
                <p className="text-xl font-bold text-[#1F2E33] mt-1">{c.value}</p>
                <p className="text-xs text-gray-500 mt-1">{c.sub}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-[#263A43]/10">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">Laderabatten dækker dit abonnement</span>
              <span className="text-sm font-bold text-[#0f5c4a]">{pct >= 100 ? '100%+' : `${pct}%`}</span>
            </div>
            <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 100 ? 'linear-gradient(90deg,#22c55e,#D4A62A)' : '#22c55e' }} />
            </div>
            {hasSurplus && (
              <p className="text-center text-sm text-[#0f5c4a] font-semibold mt-3">
                + {fmt(surplus)} kr./år i overskud modregnet din el-regning – el-aftalen er reelt gratis med elbil
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}